import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import type { Toast as ToastType } from '../hooks/useToast';

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const colorMap = {
  success: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200',
  error: 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200',
  warning: 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
  info: 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
};

interface Props {
  toasts: ToastType[];
  onRemove: (id: number) => void;
}

export function ToastContainer({ toasts, onRemove }: Props) {
  if (toasts.length === 0) return null;

  return (
    // 宽度用「视口宽 − 两侧各 16px」而不是 `w-full`：后者取的是**视口宽度的 100%**，
    // 而容器只锚在 right-4、没有左侧约束 —— 屏宽不足 max-w-sm + 16 时（大部分手机
    // 都比 400px 窄），左边缘被顶到 −16px，整条提示左端直接跑出屏幕。
    // 宽屏仍由 max-w-sm 收住。
    //
    // 纵向：移动端有一条 `sticky top-0 h-12` 的顶栏，左上角是菜单按钮；提示是
    // z-[100]，压在顶栏之上，top-4 正好盖住那个按钮。移动端下移到顶栏之下
    // （lg 起顶栏隐藏，恢复原来的 top-4）。
    <div className="fixed top-16 lg:top-4 right-4 z-[100] flex flex-col gap-2
                    w-[calc(100vw-2rem)] max-w-sm pointer-events-none">
      {toasts.map((toast) => {
        const Icon = iconMap[toast.type];
        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-lg ${colorMap[toast.type]} ${toast.exiting ? 'toast-exit' : 'toast-enter'}`}
          >
            <Icon className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{toast.title}</p>
              {toast.message && (
                <p className="text-xs mt-1 opacity-80">{toast.message}</p>
              )}
            </div>
            <button
              onClick={() => onRemove(toast.id)}
              className="shrink-0 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
